import { useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, resources } from '@/mocks/categories';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();

  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];
  const filteredResources =
    currentCategory.slug === 'all'
      ? resources
      : resources.filter((r) => r.categorySlug === currentCategory.slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    document.title = `${currentCategory.name} - 91网资源导航`;
  }, [currentCategory.name]);

  return (
    <>
      <Navbar />
      <main className="pt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-8">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: currentCategory.name },
            ]}
          />

          <div className="mt-6 mb-10">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
              {currentCategory.name}
            </h1>
            <p className="text-sm md:text-base text-foreground-500 max-w-2xl">
              {currentCategory.description}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-8">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  currentCategory.slug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
                <span className="ml-1.5 text-xs opacity-70">
                  {cat.count}
                </span>
              </Link>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filteredResources.map((item) => (
              <Link
                key={item.id}
                to={item.link}
                className="group rounded-lg overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
              >
                <div className="relative w-full h-48 overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
                    {item.tags.slice(0, 2).map((tag) => (
                      <span
                        key={tag}
                        className="px-2.5 py-0.5 bg-background-50/90 text-xs text-foreground-700 rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="p-5">
                  <h2 className="text-base font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                    {item.title}
                  </h2>
                  <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2 mb-3">
                    {item.description}
                  </p>
                  <div className="flex items-center justify-between text-xs text-foreground-400">
                    <div className="flex items-center gap-1">
                      <i className="ri-star-fill text-accent-500 text-sm"></i>
                      <span>{item.rating}</span>
                    </div>
                    <span>{item.users}人使用</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {filteredResources.length === 0 && (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                <i className="ri-inbox-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该分类暂无资源</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}